import { type DeviceDetails } from '../../services/device-casings/DeviceDetails.js';
import { Modal } from '../';
import { useEffect, useState, useRef } from 'react';
import { ClickLoadingIndicator } from '../';
import { onKeyDown, scrollDown, scrollUp, resetYPos } from '../../phone.js';
import upChevron from '../../assets/images/up-chevron.svg';
import './FullScreenDevicePreview.css';

export interface FullScreenDevicePreviewProps {
  name?: string;
  fullUrl: string;
  deviceDetails: DeviceDetails | undefined;
  onClose?: () => void;
}

export function FullScreenDevicePreview({
  name,
  fullUrl,
  deviceDetails,
  onClose,
}: FullScreenDevicePreviewProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const layoutRef = useRef<HTMLDivElement | null>(null);

  const closeModal = () => {
    setIsOpen(false);
    onClose && onClose();
    resetYPos();
  };

  useEffect(() => {
    const currentHeader = headerRef.current;
    if (!currentHeader) return;
    const observer = new ResizeObserver(() => {
      setHeaderHeight(currentHeader.offsetHeight ?? 0);
    });
    observer.observe(currentHeader);
    setHeaderHeight(currentHeader.offsetHeight);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    document.body.classList.add('fullscreen-open');
    const tooltips = document.querySelectorAll('.tooltip-high-z');
    tooltips.forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });
    return () => {
      document.body.classList.remove('fullscreen-open');
    };
  }, []);

  let transparentBg = false;
  let styledContainer = null;

  if (deviceDetails) {
    const { screenHeight, screenWidth, deviceBorderRadius } = deviceDetails;
    const {
      backgroundDeviceUrl,
      containerHeight,
      containerLeft,
      containerTop,
      containerWidth,
      bgXPos,
      bgYPos,
      borderRadius,
    } = deviceDetails.container;

    if (backgroundDeviceUrl) transparentBg = true;

    const wrapperStyle = {
      '--fp-scale': scale.toString(),
      '--fp-header-offset': `${headerHeight + 20}px`,
    } as React.CSSProperties;

    const ensurePath = (p?: string) => {
      if (!p) return '';
      return p.startsWith('/') ? p : `/${p}`;
    };

    const normalize = (v?: string) => {
      if (!v) return '0';
      return v;
    };

    const screenVars = {
      '--fp-device-bg': `url(${ensurePath(backgroundDeviceUrl)}) no-repeat ${bgXPos} ${bgYPos}`,
      '--fp-screen-width': normalize(screenWidth),
      '--fp-screen-height': normalize(screenHeight),
      '--fp-device-border-radius': normalize(deviceBorderRadius ?? '0'),
    } as React.CSSProperties;

    const containerVars = {
      '--fp-container-width': normalize(containerWidth),
      '--fp-container-height': normalize(containerHeight),
      '--fp-container-left': normalize(containerLeft),
      '--fp-container-top': normalize(containerTop),
      '--fp-container-border-radius': normalize(borderRadius),
    } as React.CSSProperties;

    styledContainer = (
      <div
        className="device-preview-wrapper"
        style={wrapperStyle}
      >
        <div className="device-preview-stage">
          <div
            id="thirdparty_litmus_mobile_screen"
            style={screenVars}
          >
            <div
              id="thirdparty_litmus_mobile_container"
              style={containerVars}
            >
              <div id="thirdparty_litmus_screen">
                <img
                  src={fullUrl}
                  id="thirdparty_litmus_screenimg"
                />
              </div>
            </div>
          </div>
          <div
            className="device-preview-controls"
            role="controls"
          >
            <div className="p-5 flex flex-col gap-2">
              <button
                onClick={scrollUp}
                className="bg-black mt-16 p-3 border-blue-500 border-2 rounded-lg"
              >
                <img
                  src={upChevron}
                  alt="up-chevron"
                />
              </button>
              <button
                onClick={scrollDown}
                className="p-3 border-blue-500 border-2 bg-black rounded-lg"
              >
                <img
                  src={upChevron}
                  alt="down-chevron"
                  className="rotate-180"
                />
              </button>
            </div>
            <div className="p-5 flex flex-col gap-2 w-64">
              <p className="text-white glow-text filter border-blue-500 border-2">
                You can either use up and down arrows on your keyboard or use the buttons to scroll
                the device content.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const image = new Image();
    image.src = fullUrl;
    image.onload = () => {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    };
  }, [fullUrl]);

  useEffect(() => {
    const updateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (!deviceDetails) {
        setScale(1);
        if (layoutRef.current) {
          layoutRef.current.style.setProperty('--fp-device-width', '0px');
          layoutRef.current.style.setProperty('--fp-scale', '1');
        }
        return;
      }
      const screenW = parseInt(deviceDetails.screenWidth?.replace(/px$/i, '') || '0', 10);
      const screenH = parseInt(deviceDetails.screenHeight?.replace(/px$/i, '') || '0', 10);
      const headerOffset = headerHeight + 40;
      const controlsReserve = viewportWidth > 900 ? 260 : 0;
      const availableW = viewportWidth - controlsReserve - 40;
      const availableH = viewportHeight - headerOffset - 40;
      if (!screenW || !screenH) {
        setScale(1);
        return;
      }
      const widthScale = availableW / screenW;
      const heightScale = availableH / screenH;
      const nextScale = Math.min(widthScale, heightScale, 1);
      setScale(nextScale);
      if (layoutRef.current) {
        layoutRef.current.style.setProperty('--fp-device-width', `${screenW}px`);
        layoutRef.current.style.setProperty('--fp-scale', `${nextScale}`);
      }
    };
    window.addEventListener('resize', updateScale);
    updateScale();
    return () => window.removeEventListener('resize', updateScale);
  }, [deviceDetails, headerHeight]);

  return (
    <>
      {isLoading ? (
        <ClickLoadingIndicator
          isLoading={isLoading}
          type={'page'}
        />
      ) : (
        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          title={
            <div
              ref={headerRef}
              className="email-client-header flex flex-col items-center w-full"
            >
              <div className="email-client-header-text text-center">
                <span className="email-client-label">Email client: </span>
                <span className="email-client-name">{name || 'Untitled'}</span>
              </div>
            </div>
          }
          transparent={transparentBg}
        >
          <div
            ref={layoutRef}
            className={`preview-root-container ${deviceDetails ? 'has-device-frame' : ''}`}
            style={{ '--fp-header-offset': `${headerHeight + 20}px` } as React.CSSProperties}
          >
            <div
              className="preview-scroll-container"
              onKeyDown={onKeyDown}
              data-testid="full-screen-device-preview"
            >
              {deviceDetails ? (
                styledContainer
              ) : (
                <div className="preview-image-wrapper">
                  <img
                    data-testid="full-screen-device-preview-image"
                    src={fullUrl}
                    alt="fullUrl"
                    className="preview-image"
                  />
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
